import { useState } from 'react'
import { useOutreachContactsGrid, type OutreachContactItem } from '@/hooks/useOutreach'
import { Search, Linkedin, Phone, Mail, ChevronLeft, ChevronRight, Star, Building2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { OUTREACH_STATUS_CONFIG } from '@/types/outreach'
import type { OutreachStatus } from '@/types/outreach'

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'not_contacted', label: 'Não contatado' },
  { value: 'invite_sent', label: 'Convite enviado' },
  { value: 'connected', label: 'Conectado' },
  { value: 'msg_sent', label: 'Mensagem enviada' },
  { value: 'replied', label: 'Respondeu' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'qualified', label: 'Qualificado' },
  { value: 'opportunity', label: 'Oportunidade' },
  { value: 'not_interested', label: 'Não interessado' },
]

export default function OutreachContactsGrid({ onContactClick }: { onContactClick?: (item: OutreachContactItem) => void }) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const perPage = 30

  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
  const handleSearchChange = (val: string) => {
    setSearch(val)
    if (timer) clearTimeout(timer)
    const t = setTimeout(() => {
      setDebouncedSearch(val)
      setPage(1)
    }, 400)
    setTimer(t)
  }

  const { data, isLoading } = useOutreachContactsGrid({
    search: debouncedSearch || undefined,
    outreach_status: statusFilter || undefined,
    page,
    per_page: perPage,
  })

  const items = data?.items || []
  const total = data?.total || 0
  const totalPages = data?.total_pages || 1

  return (
    <div className="bg-card border rounded-lg overflow-hidden">
      {/* Search + Filters Bar */}
      <div className="p-3 border-b flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar por nome, email, empresa, cargo, status, CNPJ..."
            className="w-full pl-9 pr-8 py-2 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          {search && (
            <button onClick={() => { setSearch(''); setDebouncedSearch(''); setPage(1) }} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="text-xs text-muted-foreground self-center whitespace-nowrap">
          {total} contato{total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-muted-foreground text-xs uppercase">
              <th className="p-2 pl-3 text-left font-medium">Contato</th>
              <th className="p-2 text-left font-medium hidden lg:table-cell">Cargo</th>
              <th className="p-2 text-left font-medium">Empresa</th>
              <th className="p-2 text-left font-medium hidden md:table-cell">Email</th>
              <th className="p-2 text-center font-medium">Status</th>
              <th className="p-2 text-left font-medium hidden xl:table-cell">Cadência</th>
              <th className="p-2 text-center font-medium">Links</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Nenhum contato encontrado</td></tr>
            ) : items.map((item) => {
              const statusCfg = OUTREACH_STATUS_CONFIG[item.outreach_status as OutreachStatus]
              return (
                <tr
                  key={`${item.contact_id}-${item.lead_id}`}
                  onClick={() => onContactClick?.(item)}
                  className={cn(
                    'border-b hover:bg-accent/30 cursor-pointer transition-colors',
                    !item.is_owner && 'opacity-60',
                  )}
                >
                  <td className="p-2 pl-3">
                    <div className="flex items-center gap-2">
                      {item.is_primary && <Star className="w-3 h-3 text-amber-500 flex-shrink-0" fill="currentColor" />}
                      <div>
                        <div className="font-medium truncate max-w-[200px]">{item.full_name || '-'}</div>
                        {item.lead_title && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">{item.lead_title}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 text-muted-foreground truncate max-w-[150px] hidden lg:table-cell">{item.job_title || '-'}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <span className="truncate max-w-[180px]">{item.company_name}</span>
                    </div>
                  </td>
                  <td className="p-2 text-muted-foreground truncate max-w-[200px] hidden md:table-cell">{item.email || '-'}</td>
                  <td className="p-2 text-center">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: (statusCfg?.color || '#666') + '20', color: statusCfg?.color || '#666' }}
                    >
                      {statusCfg?.title || item.outreach_status}
                    </span>
                  </td>
                  <td className="p-2 text-xs text-muted-foreground hidden xl:table-cell">
                    {item.cadence_name ? (
                      <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full">{item.cadence_name} ({item.cadence_step})</span>
                    ) : '-'}
                  </td>
                  <td className="p-2">
                    <div className="flex items-center justify-center gap-1.5">
                      {item.linkedin_url && (
                        <a href={item.linkedin_url.startsWith('http') ? item.linkedin_url : `https://${item.linkedin_url}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-6 h-6 rounded flex items-center justify-center hover:opacity-80" style={{ backgroundColor: '#0A66C2' }} title="LinkedIn">
                          <Linkedin className="w-3 h-3 text-white" />
                        </a>
                      )}
                      {item.whatsapp && (
                        <a href={`https://wa.me/${item.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-6 h-6 rounded flex items-center justify-center bg-green-600 hover:opacity-80" title="WhatsApp">
                          <Phone className="w-3 h-3 text-white" />
                        </a>
                      )}
                      {item.email && (
                        <a href={`mailto:${item.email}`} onClick={(e) => e.stopPropagation()} className="w-6 h-6 rounded flex items-center justify-center bg-blue-600 hover:opacity-80" title={item.email}>
                          <Mail className="w-3 h-3 text-white" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-3 border-t flex items-center justify-between text-xs text-muted-foreground">
          <span>Página {page} de {totalPages} ({total} contatos)</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1} className="p-1.5 rounded border hover:bg-accent disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} className="p-1.5 rounded border hover:bg-accent disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  )
}
